import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import type { CustomerLookupEntry } from './types';

/**
 * M19 T3 shared filter components for the transactional list views:
 *
 *   - `CustomerFilter`    — universal email/name typeahead. One input; queries
 *                           the DO `/admin/customers/lookup` endpoint (debounced
 *                           350ms), shows a suggestion dropdown, and reports the
 *                           chosen `cus_` id + a display label up via onChange.
 *   - `DateRangeFilter`   — preset dropdown (All time / Last 7/30/90 days /
 *                           This year) + optional custom from/to date inputs.
 *                           Reports `from`/`to` Unix seconds.
 *   - `PresetChips`       — one-click saved-view chips that set multiple
 *                           filters at once (per the T3.5 persona table).
 *
 * These are presentation components; each view owns its filter state and passes
 * it down. The customer typeahead is the single highest-value filter — every
 * transactional view shares it.
 */

// ─── CustomerFilter ────────────────────────────────────────────────────────

interface LookupResponse {
  items: CustomerLookupEntry[];
}

/**
 * Universal Customer filter. Type an email or (partial) name → debounced
 * lookup → suggestion dropdown → pick → onChange(stripeCustomerId, label).
 * Shows the selected customer as a removable pill. When `selected` is null the
 * input is empty and editable.
 */
export function CustomerFilter({
  selected,
  selectedLabel,
  onSelect,
  onClear,
}: {
  /** Currently-selected customer id (null = none). */
  selected: string | null;
  /** Display label for the selected customer (e.g. "Alice <a@x.com>"). */
  selectedLabel: string | null;
  onSelect: (customerId: string, label: string) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const fetcher = useMemo(
    () => (signal: AbortSignal) =>
      debounced
        ? api.get<LookupResponse>(`admin/customers/lookup?q=${encodeURIComponent(debounced)}`, { signal })
        : Promise.resolve<LookupResponse>({ items: [] }),
    [debounced]
  );
  const { data, loading } = useAsync(fetcher, [debounced]);
  const suggestions = data?.items ?? [];

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const labelFor = (c: CustomerLookupEntry) =>
    c.name ? `${c.name} <${c.email}>` : c.email;

  // Selected state: render a removable pill + a hidden input to keep layout.
  if (selected) {
    return (
      <div className="flex items-center gap-1 rounded-md bg-brand-50 px-2 py-1 text-xs text-brand-700">
        <span className="font-medium">Customer:</span>
        <span className="max-w-[16rem] truncate">{selectedLabel ?? selected}</span>
        <button
          type="button"
          onClick={onClear}
          className="ml-1 text-brand-500 hover:text-brand-700"
          title="Clear customer filter"
          aria-label="Clear customer filter"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="search"
        placeholder="Filter by customer…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => debounced && setOpen(true)}
        className="input w-full max-w-xs text-sm"
      />
      {open && debounced && (
        <div className="absolute z-10 mt-1 max-h-64 w-full max-w-xs overflow-auto rounded-md border border-ink-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-xs text-ink-400">Searching…</div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-ink-400">No matches.</div>
          )}
          {!loading &&
            suggestions.map((c) => (
              <button
                key={c.stripeCustomerId}
                type="button"
                onClick={() => {
                  onSelect(c.stripeCustomerId, labelFor(c));
                  setQuery('');
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-xs hover:bg-ink-50"
              >
                <span className="font-medium text-ink-800">
                  {c.name ?? c.email}
                </span>
                {c.name && (
                  <span className="ml-1 text-ink-400">&lt;{c.email}&gt;</span>
                )}
                <span className="ml-1 text-ink-300">{c.stripeCustomerId}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ─── DateRangeFilter ───────────────────────────────────────────────────────

/** Preset date ranges. `value` is the dropdown key; 'all' = no filter. */
const DATE_PRESETS: { value: string; label: string; days: number | null; }[] = [
  { value: 'all', label: 'All time', days: null },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: 'year', label: 'This year', days: null },
];

/** Today's date as yyyy-MM-dd for the custom date inputs' max attr. */
function todayISO(): string {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

/** Convert a yyyy-MM-dd input value to a Unix-seconds start-of-day. */
function isoToUnix(iso: string, endOfDay = false): number | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return undefined;
  return endOfDay ? Math.floor(d.getTime() / 1000) + 86399 : Math.floor(d.getTime() / 1000);
}

export interface DateRangeValue {
  from?: number;
  to?: number;
}

/**
 * Date-range filter: a preset dropdown (the common case) + optional custom
 * from/to date inputs. Reports the selected `from`/`to` as Unix seconds via
 * onChange. The preset dropdown also drives a hidden `preset` state so the
 * custom inputs are only surfaced when the user picks "Custom".
 */
export function DateRangeFilter({
  value,
  onChange,
}: {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
}) {
  const [preset, setPreset] = useState<string>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const applyPreset = (key: string) => {
    setPreset(key);
    setCustomFrom('');
    setCustomTo('');
    const nowSec = Math.floor(Date.now() / 1000);
    if (key === 'all') {
      onChange({});
    } else if (key === 'year') {
      const start = Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000;
      onChange({ from: Math.floor(start) });
    } else {
      const days = Number(key);
      if (Number.isFinite(days)) onChange({ from: nowSec - days * 86400 });
    }
  };

  const today = todayISO();
  const fromISO = new Date((value.from ?? 0) * 1000).toISOString().slice(0, 10);

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label className="flex items-center gap-1.5 text-ink-600">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
          Date
        </span>
        <select
          value={preset}
          onChange={(e) => applyPreset(e.target.value)}
          className="input max-w-[10rem] text-sm"
        >
          {DATE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
          <option value="custom">Custom…</option>
        </select>
      </label>
      {preset === 'custom' && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            max={customTo || today}
            value={customFrom}
            onChange={(e) => {
              setCustomFrom(e.target.value);
              const from = isoToUnix(e.target.value);
              const to = isoToUnix(customTo, true);
              onChange({ from, to });
            }}
            className="input max-w-[9rem] text-xs"
            aria-label="From date"
          />
          <span className="text-ink-400">–</span>
          <input
            type="date"
            min={fromISO || undefined}
            max={today}
            value={customTo}
            onChange={(e) => {
              setCustomTo(e.target.value);
              const to = isoToUnix(e.target.value, true);
              onChange({ from: value.from, to });
            }}
            className="input max-w-[9rem] text-xs"
            aria-label="To date"
          />
        </div>
      )}
    </div>
  );
}

// ─── TextFilter ────────────────────────────────────────────────────────────

/**
 * A labeled free-text filter input (e.g. "Find by name", "Find by email"). The
 * value is debounced 350ms by the parent (reports via onChange on each keystroke
 * here; the parent owns the debounce/param mapping). Renders a labeled input
 * with an optional clear button when non-empty.
 */
export function TextFilter({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-ink-600">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </span>
      <input
        type="search"
        placeholder={placeholder ?? label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input max-w-[16rem] text-sm"
      />
    </label>
  );
}
